import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header/Header";
import CtaBand from "../../components/Home/CtaBand";
import ContactForm from "../../components/Forms/ContactForm";
import ChatWidget from "../../components/ChatWidget/ChatWidget";
import TitleBand from "../../components/TitleBand/TitleBand";
import SolutionExplainer from "./SolutionExplainer";
import { findSolution, groupPath, itemPath, useSolutionTitles } from "./content";
import { useInView } from "../../util/useInView";
import Seo from "../../seo/Seo";
import { ORGANIZATION_REF } from "../../seo/site";
import "./solucao.css";

const pad = (n: number) => String(n + 1).padStart(2, "0");

// /solucoes/:groupSlug/:itemSlug — o que é, benefícios, como a Calriz entrega, para quem é e os outros itens do tópico.
// A key remonta a página ao trocar de item, para as animações de entrada rodarem de novo.
export default function SolutionItemPage() {
  const { groupSlug, itemSlug } = useParams();
  return <SolutionItem key={`${groupSlug}/${itemSlug}`} groupSlug={groupSlug} itemSlug={itemSlug} />;
}

function SolutionItem({ groupSlug, itemSlug }: { groupSlug?: string; itemSlug?: string }) {
  const { t } = useTranslation();
  const { group, item } = findSolution(groupSlug, itemSlug);
  const { groupTitle, itemTitle } = useSolutionTitles();
  const [benefitsRef, benefitsState] = useInView<HTMLUListElement>();
  const [stepsRef, stepsState] = useInView<HTMLOListElement>(0.3);

  if (!group) return <Navigate to="/solucoes" replace />;
  if (!item) return <Navigate to={groupPath(group)} replace />;
  if (item.href) return <Navigate to={item.href} replace />;

  const title = itemTitle(group, item);
  const topic = groupTitle(group);
  const related = group.items.filter((other) => other.key !== item.key);

  return (
    <div className="cz-page cz-scope cz-sol-page">
      <Seo
        title={`${title} | ${topic}`}
        description={item.tagline}
        path={itemPath(group, item)}
        jsonLd={{ "@type": "Service", name: title, serviceType: topic, description: item.tagline, provider: ORGANIZATION_REF }}
      />
      <Header />
      <main>
        <TitleBand
          crumbs={[
            { label: t("header.solutions", "Soluções"), to: "/solucoes" },
            { label: topic, to: groupPath(group) },
          ]}
          title={title}
          icon={item.icon}
        />

        <section className="cz-sol-section cz-sol-what" aria-labelledby="cz-sol-what-title">
          <div className="cz-sol-what__text">
            <span className="cz-eyebrow">{t("solutions_page.what_eyebrow", "O que é")}</span>
            <h2 className="cz-sol-h2" id="cz-sol-what-title">
              {t("solutions_page.what_title", { name: title, defaultValue: "Entenda {{name}}" })}
            </h2>
            {item.what.map((paragraph) => (
              <p key={paragraph} className="cz-sol-lead">{paragraph}</p>
            ))}
          </div>
          <SolutionExplainer icon={item.icon} label={title} concepts={item.concepts} />
        </section>

        <section className="cz-sol-band" aria-labelledby="cz-sol-benefits-title">
          <div className="cz-sol-section">
            <div className="cz-sol-head">
              <span className="cz-eyebrow">{t("solutions_page.benefits_eyebrow", "Benefícios")}</span>
              <h2 className="cz-sol-h2" id="cz-sol-benefits-title">
                {t("solutions_page.benefits_title", "O que muda na sua operação")}
              </h2>
            </div>
            <ul ref={benefitsRef} className={`cz-sol-benefits${benefitsState}`}>
              {item.benefits.map((benefit, i) => (
                <li key={benefit.title} className="cz-sol-benefit" style={{ "--i": i } as React.CSSProperties}>
                  <span className="cz-outline-num cz-outline-num--navy cz-sol-benefit__num" aria-hidden="true">{pad(i)}</span>
                  <h3 className="cz-sol-benefit__title">{benefit.title}</h3>
                  <p className="cz-sol-benefit__text">{benefit.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="cz-sol-section" aria-labelledby="cz-sol-steps-title">
          <div className="cz-sol-head">
            <span className="cz-eyebrow">{t("solutions_page.steps_eyebrow", "Como funciona")}</span>
            <h2 className="cz-sol-h2" id="cz-sol-steps-title">
              {t("solutions_page.steps_title", "Como a Calriz entrega")}
            </h2>
          </div>
          <ol
            ref={stepsRef}
            className={`cz-sol-steps${stepsState}`}
            style={{ "--count": item.steps.length } as React.CSSProperties}
          >
            {item.steps.map((step, i) => (
              <li key={step.title} className="cz-sol-step" style={{ "--i": i } as React.CSSProperties}>
                <span className="cz-sol-step__dot" aria-hidden="true">{pad(i)}</span>
                <h3 className="cz-sol-step__title">{step.title}</h3>
                <p className="cz-sol-step__text">{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="cz-sol-section cz-sol-fit">
          <div className="cz-sol-card">
            <h2 className="cz-sol-card__title">{t("solutions_page.audience_title", "Para quem é")}</h2>
            <ul className="cz-sol-audience">
              {item.audience.map((who) => (
                <li key={who}>
                  <span className="cz-sol-audience__icon" aria-hidden="true">
                    <Check size={14} strokeWidth={2.5} />
                  </span>
                  {who}
                </li>
              ))}
            </ul>
          </div>

          {related.length > 0 && (
            <div className="cz-sol-card">
              <h2 className="cz-sol-card__title">
                {t("solutions_page.related_title", { name: topic, defaultValue: "Mais em {{name}}" })}
              </h2>
              <ul className="cz-sol-related">
                {related.map((other) => {
                  const Icon = other.icon;
                  return (
                    <li key={other.key}>
                      <Link to={itemPath(group, other)} className="cz-sol-related__link">
                        <span className="cz-sol-related__icon" aria-hidden="true">
                          <Icon size={18} />
                        </span>
                        <span>{itemTitle(group, other)}</span>
                        <ArrowRight size={16} className="cz-sol-related__arrow" aria-hidden="true" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </section>

        <CtaBand />
        <ContactForm />
      </main>
      <ChatWidget />
    </div>
  );
}
